"use client";

import classNames from "classnames";
import PropTypes from "prop-types";
import elementType from "prop-types-extra/lib/elementType";
import { useContext, useEffect, useState } from "react";

import TabContainerContext from "./TabContainerContext";
import TabContentContext from "./TabContentContext";
import { prefix, bsClass as setBsClass, splitBsPropsAndOmit } from "./utils/bootstrapUtils";

const propTypes = {
  componentClass: elementType,

  /**
   * Sets a default animation strategy for all children `<TabPane>`s. Use
   * `false` to disable, `true` to enable the default `<Fade>` animation or any
   * `<Transition>` component.
   */
  animation: PropTypes.oneOfType([
    PropTypes.bool, elementType,
  ]),

  /**
   * Wait until the first "enter" transition to mount tabs (add them to the DOM)
   */
  mountOnEnter: PropTypes.bool,

  /**
   * Unmount tabs (remove it from the DOM) when they are no longer visible
   */
  unmountOnExit: PropTypes.bool,
};

const defaultProps = {
  componentClass: "div",
  animation: true,
  mountOnEnter: false,
  unmountOnExit: false,
};

function TabContent(props) {
  const {
    bsClass, animation, mountOnEnter, unmountOnExit,
  } = props;
  const [activeKey, setActiveKey] = useState(null);
  const [activeChild, setActiveChild] = useState(null);
  let isUnmounted = false;
  const tabContainerCtx = useContext(TabContainerContext);

  useEffect(() => {
    if (!animation && activeChild) {
      setActiveKey(null);
      setActiveChild(null);
    }

    return () => {
      isUnmounted = true;
    };
  }, [activeKey, activeChild]);

  const getContainerActiveKey = () => {
    // todo:context remove context
    const tabContainer = tabContainerCtx.$bs_tabContainer;
    return tabContainer && tabContainer.activeKey;
  };

  const handlePaneEnter = (child: any, childKey: any) => {
    if (!animation) {
      return false;
    }

    // It's possible that this child should be transitioning out.
    if (childKey !== getContainerActiveKey()) {
      return false;
    }

    setActiveKey(childKey);
    setActiveChild(child);

    return true;
  };

  const handlePaneExited = (child: any) => {
    // This might happen as everything is unmounting.
    if (isUnmounted) {
      return;
    }

    if (activeChild !== child) {
      setActiveChild(null);
    } else {
      setActiveKey(null);
      setActiveChild(null);
    }
  };

  const containerActiveKey = getContainerActiveKey();

  useEffect(() => {
    setActiveKey(containerActiveKey);
  }, [containerActiveKey]);

  const contextActiveKey = activeKey != null ? activeKey : containerActiveKey;
  const exiting = activeKey != null && activeKey !== containerActiveKey;

  const tabContentValue = {
    $bs_tabContent: {
      bsClass,
      animation,
      activeKey: contextActiveKey,
      mountOnEnter,
      unmountOnExit,
      onPaneEnter: handlePaneEnter,
      onPaneExited: handlePaneExited,
      exiting,
    },
  };

  const { componentClass: Component, className, ...restProps } = props;
  const [bsProps, elementProps] = splitBsPropsAndOmit(restProps, [
    "animation", "mountOnEnter", "unmountOnExit",
  ]);

  return (
    <TabContentContext.Provider value={tabContentValue}>
      <Component
        {...elementProps}
        className={classNames(className, prefix(bsProps, "content"))}
      />
    </TabContentContext.Provider>
  );
}

TabContent.propTypes = propTypes;
TabContent.defaultProps = defaultProps;

export default setBsClass("tab", TabContent);
